import { Suspense, useState } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Button } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { RocketSVG } from '@svgComponents/rocketSVG/RocketSVG';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useGetInterestOnlyExtensionApprovedBannerData } from './useGetInterestOnlyExpensionApprovedBannerData';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { Rate } from '../../../ui/rate';
import { DrawdownActionPanel } from '../drawdownActionPanel/DrawdownActionPanel';

export const InterestOnlyExtensionApprovedBanner = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const [drawdownPanelIsOpen, setDrawdownPanelIsOpen] = useState(false);

    return (
        <>
            <div className="col-span-5 grid grid-flow-dense grid-cols-7 gap-m rounded-s border border-success-mid bg-success-lt p-l max-sm:col-span-full">
                <div className="col-span-1 m-auto max-sm:col-span-full">
                    <RocketSVG className="h-4xl w-4xl text-success-drk" />
                </div>
                <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                    <Text className="text-xl font-bold">
                        <FormattedMessage
                            id="_.interest_only_extension_banner.approved_title"
                            defaultMessage="You're approved!"
                        />
                    </Text>
                    <p
                        data-testid="approved-alert-description"
                        className="m-0 font-reg text-white"
                    >
                        <Suspense fallback={MainContentPending}>
                            <BannerContent accountId={accountId} />
                        </Suspense>
                    </p>
                </div>
                <div className="col-span-4 max-sm:col-span-full max-sm:text-center min-m:col-start-2">
                    <Button onClick={() => setDrawdownPanelIsOpen(true)}>
                        <FormattedMessage
                            id="_.interest_only_extension_banner.cta_text"
                            defaultMessage="Extend my interest-only period"
                        />
                    </Button>
                </div>
            </div>
            {drawdownPanelIsOpen && (
                <div className="col-span-5 mt-[-36px]">
                    <DrawdownActionPanel
                        accountId={accountId}
                        setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                    />
                </div>
            )}
        </>
    );
};

const BannerContent = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const { topUpStatus, interestRate, approvalAmount, timeoutDate } =
        useGetInterestOnlyExtensionApprovedBannerData(accountId);

    switch (topUpStatus) {
        case 'INTEREST_ONLY_EXTENSION_APPROVED':
            return (
                <Text className="text-l">
                    <FormattedMessage
                        id="_.interest_only_extension_banner.interestOnlyExtenionDescription"
                        defaultMessage="You can extend your interest-only period by 3 months keeping your interest rate at {interestRate} per month before {timeoutDate}."
                        values={{
                            interestRate: <Rate value={interestRate} />,
                            timeoutDate: (
                                <FormattedDate
                                    value={timeoutDate}
                                    day="2-digit"
                                    month="short"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                </Text>
            );
        case 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS':
            return (
                <Text className="text-l">
                    <FormattedMessage
                        id="_.interest_only_extension_banner.interestOnlyExtenionWithFundsDescription"
                        defaultMessage="You can extend your interest-only period by 3 months and draw down up to {amount} with an interest rate of {interestRate} per month before {timeoutDate}."
                        values={{
                            amount: (
                                <FormattedCurrency value={approvalAmount} />
                            ),
                            interestRate: <Rate value={interestRate} />,
                            timeoutDate: (
                                <FormattedDate
                                    value={timeoutDate}
                                    day="2-digit"
                                    month="short"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                </Text>
            );
        default:
            return null;
    }
};
